/**
 * PickupRegistry - Registry for extensible pickup type definitions
 *
 * Manages pickup types that appear on the road (waves, salvage crates, etc.).
 * Each type defines spawn probability, visual appearance, and collection behavior.
 *
 * @example
 * import { pickupRegistry, getPickupRegistry } from './registries/PickupRegistry.js';
 *
 * // Register a pickup type
 * pickupRegistry.register('wave', {
 *     weight: 0.75,
 *     signLabel: 'NEXT LOT',
 *     model: null,
 *     particleColor: null,
 *     autoCollect: false,
 *     onCollect: (gameState, pickupData) => { ... }
 * });
 *
 * // Get a pickup type
 * const waveType = pickupRegistry.get('wave');
 *
 * // Get weighted random type
 * const randomType = pickupRegistry.getWeightedRandomType();
 */
import { Registry } from '../core/Registry.js';

/**
 * @typedef {Object} PickupTypeConfig
 * @property {string} id - Unique identifier (e.g., 'wave', 'salvage-crate')
 * @property {number} weight - Spawn probability weight (higher = more common)
 * @property {string} signLabel - Road sign display text (max 20 chars)
 * @property {string|null} model - 3D model path, or null to use default
 * @property {number|null} particleColor - Collection effect color (hex), or null for dynamic
 * @property {boolean} autoCollect - If true, execute callback immediately on touch; if false, add to inventory
 * @property {function(Object, Object): void} [onCollect] - Collection callback (gameState, pickupData)
 */

/**
 * Extended Registry for pickup type definitions
 */
export class PickupRegistry extends Registry {
    constructor() {
        super('Pickups');
        this._setupValidators();
    }

    /**
     * Set up pickup-specific validators
     * @private
     */
    _setupValidators() {
        // Required fields validator
        this.addValidator((id, def) => {
            if (!id || typeof id !== 'string') {
                return 'Pickup type must have a valid id';
            }
            return null;
        });

        // Weight must be positive number
        this.addValidator((id, def) => {
            if (def.weight === undefined || def.weight === null) {
                return 'Pickup type must have a weight';
            }
            if (typeof def.weight !== 'number' || def.weight <= 0) {
                return 'Pickup type weight must be a positive number';
            }
            return null;
        });

        // signLabel required and max 20 chars
        this.addValidator((id, def) => {
            if (!def.signLabel || typeof def.signLabel !== 'string') {
                return 'Pickup type must have a signLabel';
            }
            if (def.signLabel.length > 20) {
                return `Pickup type signLabel exceeds 20 characters (got ${def.signLabel.length})`;
            }
            return null;
        });

        // autoCollect defaults to false (handled in register override)
        // onCollect must be a function if provided
        this.addValidator((id, def) => {
            if (def.onCollect !== undefined && typeof def.onCollect !== 'function') {
                return 'Pickup type onCollect must be a function';
            }
            return null;
        });
    }

    /**
     * Register a pickup type with default values applied
     * @param {string} id - Unique identifier
     * @param {Object} definition - Pickup type configuration
     * @returns {this} The registry instance for chaining
     */
    register(id, definition) {
        // Apply defaults before validation
        const withDefaults = {
            autoCollect: false,
            model: null,
            particleColor: null,
            ...definition
        };

        return super.register(id, withDefaults);
    }

    /**
     * Get a weighted random pickup type
     * Uses weight field for selection probability
     * @returns {Object|null} Randomly selected pickup type object, or null if registry empty
     */
    getWeightedRandomType() {
        const types = this.getAll();
        if (types.length === 0) return null;

        const totalWeight = types.reduce((sum, type) => sum + type.weight, 0);
        let random = Math.random() * totalWeight;

        for (const type of types) {
            random -= type.weight;
            if (random <= 0) return type;
        }

        return types[types.length - 1]; // Fallback
    }

    /**
     * Get all pickup types sorted by weight (descending)
     * @returns {Array<Object>} Sorted array of pickup types
     */
    getAllSortedByWeight() {
        return this.getAll().sort((a, b) => b.weight - a.weight);
    }
}

// Singleton instance for app-wide use
export const pickupRegistry = new PickupRegistry();

/**
 * Get the pickup registry singleton
 * @returns {PickupRegistry} The singleton instance
 */
export function getPickupRegistry() {
    return pickupRegistry;
}
